/**
 * @license
 * Copyright (C) 2022 Anna Plodari
 * Software proprietario - Tutti i diritti riservati.
 * Registrato SIAE - Sezione OLAF.
 *
 * È vietato qualsiasi utilizzo non autorizzato, copia, modifica, estrazione,
 * riuso, rielaborazione, distribuzione, reverse engineering o decompilazione
 * del software, delle API, delle logiche applicative e dei dati del database.
 *
 * L'accesso al codice non costituisce concessione di licenza.
 */
/* eslint-disable react/function-component-definition */
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Control, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import Checkbox from '../../components/Checkbox';
import CheckboxLg from '../../components/CheckboxLg';
import PrimaryButton from '../../components/PrimaryButton';
import SecondaryLink from '../../components/SecondaryLink';
import FormContext from '../../context/FormContext';
import useProfile from '../../context/ProfileContext';
import fetchConoscenzeInformatiche from '../../lib/api/fetchConoscenzeInformatiche';
import { ConoscenzeInformatiche } from '../../components/HumanResources/human-resources';
import InputLg from '../../components/InputLg';
import { regexBlocked } from '../../lib/utils/validation';

type Inputs = {
   conoscenze_informatiche: string[],
  conoscenze_informatiche_text: string;
};

function IngegnereEnumWatched({ control }: { control: Control<Inputs> }) {
  const conos_informatiche = useWatch({
    control,
    name: 'conoscenze_informatiche',
  });

  if (conos_informatiche && conos_informatiche.includes('Altro')) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full lg:w-fit mt-3.5">
        <InputLg name="conoscenze_informatiche_text" label="" placeholder="Altre conoscenze" />
      </div>
    );
  }

  return null;
}

const InputSchema = yup.object().shape({
  conoscenze_informatiche: yup
    .array()
    .of(yup.string())
    .min(1, '*Campo obbligatorio') // obbligatorio almeno un valore
    .required('*Campo obbligatorio'),

  conoscenze_informatiche_text: yup
    .string()
    .nullable()
    .notRequired()
    .when('conoscenze_informatiche', {
      is: (val: string[] | undefined) =>
        Array.isArray(val) && val.includes('Altro'),
      then: (schema) =>
        schema.required('*Campo obbligatorio se selezioni "Altro"'),
    })
    .test(
      'no-email',
      "Non puoi inserire l'email",
      (val?: string | null) => !val || !regexBlocked.email.test(val)
    )
    .test(
      'no-phone',
      'Non puoi inserire un numero di telefono',
      (val?: string | null) => !val || !regexBlocked.phone.test(val)
    )
    .test(
      'no-address',
      'Non puoi inserire un indirizzo',
      (val?: string | null) => !val || !regexBlocked.address.test(val)
    ),
});



const Onboarding4: React.FC = () => {
  const [conoscenzeInformatiche, setConoscenzeInformatiche] = useState([]);
  const profileContext = useProfile();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      conoscenze_informatiche: profileContext.data.conoscenze_informatiche,   
       conoscenze_informatiche_text: profileContext.data.conoscenze_informatiche_text,    
    },
    resolver: yupResolver(InputSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    fetchConoscenzeInformatiche().then((response) => {
      const mapped = response.map((x: any) => ({
        key: x?.id,
        value: x?.attributes?.value,  
      }));
      const first = mapped.shift(); 
      if (first) mapped.push(first);
      setConoscenzeInformatiche(mapped);
    });
  }, []);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    profileContext.onDataUpdate(data);
    navigate('/conoscenze-linguistiche');
  };

console.log('errors',errors);

  return (
    <FormContext.Provider value={{ errors, register }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col justify-between h-screen bg-onboardingmobile4 lg:bg-onboarding4 bg-no-repeat bg-right-top lg:bg-[right_-16rem_top] xl:bg-right-top bg-cover lg:bg-contain">
          <div className="p-6 flex justify-start">
            <img src="/logo.svg" alt="" className="max-h-[30px]" />
          </div>
          <div className="flex flex-col flex-grow px-6 lg:px-28 pt-8 lg:pt-2 max-w-5xl pb-4 lg:pb-0 overflow-y-scroll hide-scrollbar">
            <div>
              <h1 className="font-primarybold text-3xl text-primary">Conoscenze informatiche</h1>
              <p className="text-lg text-textgrey" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 w-fit mt-8 custom-checkbox-mobile-cont">
              {conoscenzeInformatiche?.map((conoscenza: ConoscenzeInformatiche, idx: number) =>
                conoscenza?.value?.length > 50 ? (
                  <div className="md:col-span-2" key={idx}>
                    <CheckboxLg label={conoscenza.value} name="conoscenze_informatiche" />
                  </div>
                ) : (
                  <Checkbox label={conoscenza.value} key={idx} name="conoscenze_informatiche" />
                ),
              )}
                  <IngegnereEnumWatched control={control} />
            </div>
          </div>
          <div className="flex flex-col justify-center px-3 lg:px-28 pt-8 pb-8 relative bg-white lg:bg-transparent border-t border-t-gray-100 lg:border-t-0">
            <div className="flex justify-between items-center mobile-button-sizing">
              <SecondaryLink text="Indietro" to="/titolo-di-studio" />
              <PrimaryButton text="Avanti" />
            </div>
          </div>
        </div>
      </form>
    </FormContext.Provider>
  );
};

export default Onboarding4;